import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import { AppDialog } from '@/UI/AppDialog/AppDialog';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { color } from '@/colors';
import { useEditSubflowTemplate } from '@/queries/useSubflowTemplates';
import { useGlobalStore } from '@/state/global.state';
import { useFetchMenuItems } from '@/hooks/useFetchMenuItems';
import { SubflowTemplateType } from '@/types/api/subflowTemplate';
import { SaveNodeModalBody } from './SaveNodeModalBody';
import type { SubflowTemplateResponseDto } from '@/types/api/subflowTemplate';

const WA = I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS;

interface EditAssetDialogProps {
  asset: SubflowTemplateResponseDto;
  onClose: () => void;
}

export function EditAssetDialog({ asset, onClose }: EditAssetDialogProps) {
  const { t } = useTranslation();
  const { mutate, isPending } = useEditSubflowTemplate();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const { fetchMenuItems } = useFetchMenuItems();
  const isModel = asset.type === SubflowTemplateType.SavedModel;
  const typeLabel = isModel ? 'Model' : 'Node';

  const handleSave = useCallback(
    (name: string, description: string) => {
      mutate(
        { id: asset.id, dto: { name, description } },
        {
          onSuccess: () => {
            onClose();
            void fetchMenuItems();
            updateSnackbarData({
              text: t(WA.EDIT_SUCCESS, { type: typeLabel }),
              isOpen: true,
              icon: <CheckMarkCircleIcon width={20} height={20} />,
            });
          },
          onError: () => {
            updateSnackbarData({
              text: t(WA.EDIT_ERROR, { type: typeLabel.toLowerCase() }),
              isOpen: true,
              severity: 'error',
            });
          },
        },
      );
    },
    [asset.id, mutate, onClose, fetchMenuItems, updateSnackbarData, t, typeLabel],
  );

  return (
    <AppDialog
      open
      onClose={onClose}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: color.Black84_T,
        },
        '& .MuiPaper-root': {
          border: `1px solid ${color.White08_T}`,
        },
      }}
    >
      <SaveNodeModalBody
        onClose={onClose}
        onSave={handleSave}
        title={t(WA.EDIT_DIALOG_TITLE, { name: asset.name })}
        saveButtonLabel={t(WA.EDIT_SAVE)}
        initialName={asset.name}
        initialDescription={asset.description ?? ''}
        isSaving={isPending}
      />
      <AppXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} />
    </AppDialog>
  );
}
